import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Alert, OverlayTrigger, Tooltip, Modal, Badge, Button } from 'react-bootstrap';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'moment/locale/de';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { useAuth } from '../context/AuthContext';
import { getBackendUrl } from '../config';
import '../scss/CalendarOverride.scss';

moment.locale('de');
const localizer = momentLocalizer(moment);
const wochentageMap = { 'Mo': 1, 'Di': 2, 'Mi': 3, 'Do': 4, 'Fr': 5, 'Sa': 6, 'So': 7 };

// Custom Toolbar for Calendar
const CustomToolbar = (toolbar) => {
  const goToBack = () => { toolbar.onNavigate('PREV'); };
  const goToNext = () => { toolbar.onNavigate('NEXT'); };
  const goToCurrent = () => { toolbar.onNavigate('TODAY'); };

  const label = () => {
    const date = moment(toolbar.date);
    return (
      <span className="rbc-toolbar-label">
        {date.format('MMMM')} {date.format('YYYY')}
      </span>
    );
  };

  return (
    <div className="rbc-toolbar unified-toolbar">
      <div className="rbc-btn-group">
        <button type="button" onClick={goToBack} title="Zurück">
          <i className="bi bi-chevron-left"></i>
        </button>
        <button type="button" onClick={goToCurrent} title="Heute">
          <i className="bi bi-calendar-event"></i>
        </button>
        <button type="button" onClick={goToNext} title="Weiter">
          <i className="bi bi-chevron-right"></i>
        </button>
      </div>

      <div className="rbc-toolbar-label-wrap">{label()}</div>

      <div className="rbc-btn-group view-buttons">
        <button type="button" className={toolbar.view === 'month' ? 'rbc-active' : ''} onClick={() => toolbar.onView('month')}>
          <span className="d-none d-md-inline">Monat</span>
          <i className="bi bi-grid-3x3 d-md-none"></i>
        </button>
        <button type="button" className={toolbar.view === 'work_week' ? 'rbc-active' : ''} onClick={() => toolbar.onView('work_week')}>
          <span className="d-none d-md-inline">Woche</span>
          <i className="bi bi-view-stacked d-md-none"></i>
        </button>
        <button type="button" className={toolbar.view === 'day' ? 'rbc-active' : ''} onClick={() => toolbar.onView('day')}>
          <span className="d-none d-md-inline">Tag</span>
          <i className="bi bi-calendar-day d-md-none"></i>
        </button>
      </div>
    </div>
  );
};


const ANFRAGE_STYLE = {
  offen:      { bg: '#d97706', border: '#b45309', label: 'Ausstehend',  icon: '⏳' },
  akzeptiert: { bg: '#059669', border: '#047857', label: 'Bestätigt',   icon: '✓'  },
  abgelehnt:  { bg: '#dc2626', border: '#b91c1c', label: 'Abgelehnt',   icon: '✕'  },
  storniert:  { bg: '#6b7280', border: '#4b5563', label: 'Storniert',   icon: '⊘'  },
};

const STATUS_BADGE_MAP = {
  offen:      'warning',
  akzeptiert: 'success',
  abgelehnt:  'danger',
  storniert:  'secondary',
};

function EventDetailModal({ event, onClose, onStornieren }) {
  if (!event) return null;

  const isDozentAvail = event.type === 'availability';
  const isAnfrage = event.type === 'anfrage';
  const cfg = isAnfrage ? ANFRAGE_STYLE[event.anfrage?.status] : null;

  return (
    <Modal show={!!event} onHide={onClose} centered size="sm">
      <Modal.Header closeButton style={{ borderBottom: '3px solid ' + (cfg?.bg || '#b95c50') }}>
        <Modal.Title style={{ fontFamily: "'Lora', serif", fontSize: '1.1rem' }}>
          {isDozentAvail ? '📅 Verfügbarkeit' : `${cfg?.icon || ''} Terminanfrage`}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isDozentAvail && (
          <div>
            <p className="mb-1"><strong>Wochentag:</strong> {event.resource?.wochentag}</p>
            <p className="mb-1"><strong>Zeit:</strong> {moment(event.start).format('HH:mm')} – {moment(event.end).format('HH:mm')} Uhr</p>
            <p className="mb-0 text-muted" style={{ fontSize: '0.8rem' }}>
              Klicken Sie in der Kalenderansicht auf diesen Slot, um ihn zu löschen.
            </p>
          </div>
        )}
        {isAnfrage && event.anfrage && (
          <div>
            <div className="d-flex align-items-center gap-2 mb-3">
              <Badge bg={STATUS_BADGE_MAP[event.anfrage.status] || 'secondary'} style={{ fontSize: '0.8rem' }}>
                {cfg?.label || event.anfrage.status}
              </Badge>
            </div>
            <table style={{ width: '100%', fontSize: '0.88rem', borderCollapse: 'separate', borderSpacing: '0 4px' }}>
              <tbody>
                <tr>
                  <td style={{ color: '#6b7280', paddingRight: '1rem', whiteSpace: 'nowrap' }}>📚 Fach</td>
                  <td><strong>{event.anfrage.fach?.name || '–'}</strong></td>
                </tr>
                {event.anfrage.tutor?.vollerName && (
                  <tr>
                    <td style={{ color: '#6b7280' }}>👤 Dozent</td>
                    <td>{event.anfrage.tutor.vollerName}</td>
                  </tr>
                )}
                {event.anfrage.schueler?.vollerName && (
                  <tr>
                    <td style={{ color: '#6b7280' }}>🎓 Student</td>
                    <td>{event.anfrage.schueler.vollerName}</td>
                  </tr>
                )}
                <tr>
                  <td style={{ color: '#6b7280' }}>📅 Datum</td>
                  <td>{moment(event.start).format('ddd, DD.MM.YYYY')}</td>
                </tr>
                <tr>
                  <td style={{ color: '#6b7280' }}>🕐 Zeit</td>
                  <td>{moment(event.start).format('HH:mm')} – {moment(event.end).format('HH:mm')} Uhr</td>
                </tr>
                {event.anfrage.dauerMinuten && (
                  <tr>
                    <td style={{ color: '#6b7280' }}>⏱ Dauer</td>
                    <td>{event.anfrage.dauerMinuten} Min.</td>
                  </tr>
                )}
                {event.anfrage.preis != null && (
                  <tr>
                    <td style={{ color: '#6b7280' }}>💶 Preis</td>
                    <td>{Number(event.anfrage.preis).toFixed(2)} €</td>
                  </tr>
                )}
                {event.anfrage.beschreibung && (
                  <tr>
                    <td style={{ color: '#6b7280', verticalAlign: 'top' }}>💬</td>
                    <td style={{ fontStyle: 'italic', color: '#374151' }}>{event.anfrage.beschreibung}</td>
                  </tr>
                )}
              </tbody>
            </table>

            {event.anfrage.status === 'offen' && onStornieren && (
              <div className="mt-3 pt-3" style={{ borderTop: '1px solid #e5e7eb' }}>
                <Button
                  variant="outline-danger"
                  size="sm"
                  className="w-100"
                  onClick={() => { onStornieren(event.anfrage.id); onClose(); }}
                >
                  Anfrage stornieren
                </Button>
              </div>
            )}
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
}

export default function CalendarPage() {
  const { user, token } = useAuth();
  const isDozent = user?.role === 'dozent' || user?.role === 'tutor';

  const [availabilities, setAvailabilities] = useState([]);
  const [anfragen, setAnfragen]             = useState([]);
  const [error, setError]   = useState('');
  const [success, setSuccess] = useState('');
  const [selectedEvent, setSelectedEvent] = useState(null);
  
  // Updated threshold for better tablet experience
  const [view, setView] = useState(window.innerWidth < 992 ? 'day' : 'work_week');

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 992) {
        setView('day');
      } else {
        setView('work_week');
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchAvailabilities = useCallback(async () => {
    try {
      const res = await fetch(`${getBackendUrl()}/api/tutors/${user.id}/availability`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setAvailabilities(await res.json());
    } catch (err) { console.error(err); }
  }, [user, token]);

  const fetchAnfragen = useCallback(async () => {
    try {
      const endpoint = isDozent ? '/api/anfragen/tutor' : '/api/anfragen';
      const res = await fetch(`${getBackendUrl()}${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setAnfragen(await res.json());
    } catch (err) { console.error(err); }
  }, [isDozent, token]);

  useEffect(() => {
    if (isDozent) fetchAvailabilities();
    fetchAnfragen();
  }, [user, isDozent, fetchAvailabilities, fetchAnfragen]);

  const calendarEvents = useMemo(() => {
    const events = [];
    const activeAnfragen = anfragen
      .filter(a => a.datumZeit && a.dauerMinuten && !['storniert', 'abgelehnt'].includes(a.status))
      .map(a => ({
        start: new Date(a.datumZeit).getTime(),
        end: moment(a.datumZeit).add(a.dauerMinuten, 'minutes').toDate().getTime()
      }));

    if (isDozent) {
      for (let w = -1; w < 4; w++) {
        availabilities.forEach(av => {
          const isoDay = wochentageMap[av.wochentag];
          if (isoDay === undefined) return;
          const [startH, startM] = av.startZeit.split(':');
          const [endH, endM]     = av.endeZeit.split(':');
          
          const slotStart = moment().startOf('isoWeek').add(w, 'weeks').isoWeekday(isoDay)
            .hours(parseInt(startH, 10)).minutes(parseInt(startM, 10)).seconds(0).toDate();
          const slotEnd = moment().startOf('isoWeek').add(w, 'weeks').isoWeekday(isoDay)
            .hours(parseInt(endH, 10)).minutes(parseInt(endM, 10)).seconds(0).toDate();

          let intervals = [{ start: slotStart.getTime(), end: slotEnd.getTime() }];
          
          activeAnfragen.forEach(ex => {
            let nextIntervals = [];
            intervals.forEach(int => {
              if (ex.start < int.end && ex.end > int.start) {
                if (int.start < ex.start) nextIntervals.push({ start: int.start, end: ex.start });
                if (int.end > ex.end) nextIntervals.push({ start: ex.end, end: int.end });
              } else {
                nextIntervals.push(int);
              }
            });
            intervals = nextIntervals;
          });

          intervals.forEach((interval, idx) => {
            events.push({
              id: `avail-${av.id}-${w}-${idx}`,
              originalId: av.id,
              title: 'Verfügbar',
              start: new Date(interval.start),
              end: new Date(interval.end),
              type: 'availability',
              resource: av,
            });
          });
        });
      }
    }

    anfragen.forEach(anfrage => {
      if (!anfrage.datumZeit || !anfrage.dauerMinuten) return;
      if (isDozent && ['storniert', 'abgelehnt'].includes(anfrage.status)) return;

      const style = ANFRAGE_STYLE[anfrage.status] || ANFRAGE_STYLE.offen;
      const start = new Date(anfrage.datumZeit);
      const end   = moment(anfrage.datumZeit).add(anfrage.dauerMinuten, 'minutes').toDate();
      const tutorName  = anfrage.tutor?.vollerName;
      const schuelerName = anfrage.schueler?.vollerName;

      events.push({
        id: `anfrage-${anfrage.id}`,
        title: isDozent
          ? `${style.icon} ${anfrage.fach?.name || 'Termin'}${schuelerName ? ` · ${schuelerName}` : ''}`
          : `${style.icon} ${anfrage.fach?.name || 'Termin'}${tutorName ? ` · ${tutorName}` : ''}`,
        start, end,
        type: 'anfrage',
        anfrage,
        _style: style,
      });
    });

    return events;
  }, [availabilities, anfragen, isDozent]);

  const eventStyleGetter = (event) => {
    if (event.type === 'availability') {
      return {
        style: {
          backgroundColor: '#3b82f6',
          borderColor: '#2563eb',
          color: 'white',
          borderRadius: '5px',
          cursor: isDozent ? 'pointer' : 'default',
          fontSize: '0.78rem',
          fontWeight: 600,
        },
      };
    }
    if (event.type === 'anfrage') {
      const s = event._style;
      return {
        style: {
          backgroundColor: s.bg,
          borderColor: s.border,
          color: 'white',
          borderRadius: '5px',
          cursor: 'pointer',
          fontSize: '0.78rem',
          fontWeight: 600,
          opacity: ['abgelehnt', 'storniert'].includes(event.anfrage?.status) ? 0.55 : 1,
        },
      };
    }
    return {};
  };

  const handleSelectSlot = async ({ start, end }) => {
    if (!isDozent) return;
    const dayNames = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
    const wochentag = dayNames[start.getDay()];
    const startZeit = moment(start).format('HH:mm');
    const endeZeit  = moment(end).format('HH:mm');
    try {
      const res = await fetch(`${getBackendUrl()}/api/tutors/availability`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ wochentag, startZeit, endeZeit }),
      });
      if (res.ok) {
        fetchAvailabilities();
        setSuccess(`Verfügbarkeit für ${wochentag} hinzugefügt.`);
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError('Fehler beim Hinzufügen der Verfügbarkeit.');
        setTimeout(() => setError(''), 3000);
      }
    } catch (err) {
      setError(err.message);
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleSelectEvent = (event) => {
    if (event.type === 'availability' && isDozent) {
      if (window.confirm('Möchten Sie diese Verfügbarkeit löschen?')) {
        handleDeleteAvailability(event.originalId);
      }
      return;
    }
    setSelectedEvent(event);
  };

  const handleDeleteAvailability = async (id) => {
    try {
      const res = await fetch(`${getBackendUrl()}/api/tutors/availability/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        fetchAvailabilities();
        setSuccess('Verfügbarkeit gelöscht.');
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      setError(err.message);
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleStornieren = async (anfrageId) => {
    try {
      const res = await fetch(`${getBackendUrl()}/api/anfragen/${anfrageId}/stornieren`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        fetchAnfragen();
        setSuccess('Anfrage erfolgreich storniert.');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const errData = await res.json().catch(() => ({}));
        setError(errData.message || 'Stornierung fehlgeschlagen.');
        setTimeout(() => setError(''), 5000);
      }
    } catch (err) {
      setError(err.message);
      setTimeout(() => setError(''), 5000);
    }
  };

  const legendItems = [
    ...(isDozent ? [{ color: '#3b82f6', label: 'Meine Verfügbarkeit' }] : []),
    { color: ANFRAGE_STYLE.offen.bg,      label: 'Ausstehend' },
    { color: ANFRAGE_STYLE.akzeptiert.bg, label: 'Bestätigt'  },
    { color: ANFRAGE_STYLE.abgelehnt.bg,  label: 'Abgelehnt'  },
    { color: ANFRAGE_STYLE.storniert.bg,  label: 'Storniert'  },
  ];

  return (
    <div className="calendar-page p-2 p-md-4" style={{ backgroundColor: '#fcfaf8', minHeight: '100vh' }}>
      <div className="d-flex align-items-center justify-content-between mb-3 flex-column flex-md-row gap-3">
        <div className="d-flex align-items-center gap-2">
          <h2 className="mb-0" style={{ color: '#2c3e50', fontWeight: 700 }}>Mein Kalender</h2>
          <Badge bg="light" text="dark" pill className="border">
            {isDozent ? 'Dozent' : 'Student'}
          </Badge>
        </div>

        <div className="d-flex flex-wrap justify-content-center gap-2 gap-md-3" style={{ fontSize: '0.75rem' }}>
          {legendItems.map(item => (
            <span key={item.label} className="d-flex align-items-center gap-1" style={{ color: '#6b7280' }}>
              <span style={{
                display: 'inline-block', width: 10, height: 10,
                borderRadius: '50%', backgroundColor: item.color, flexShrink: 0,
              }} />
              {item.label}
            </span>
          ))}
        </div>
      </div>

      {error   && <Alert variant="danger"  onClose={() => setError('')}   dismissible>{error}</Alert>}
      {success && <Alert variant="success" onClose={() => setSuccess('')} dismissible>{success}</Alert>}

      <div className="calendar-container shadow-sm p-1 p-md-3 bg-white" style={{ height: window.innerWidth < 768 ? '550px' : '750px', borderRadius: '16px' }}>
        <Calendar
          localizer={localizer}
          events={calendarEvents}
          startAccessor="start"
          endAccessor="end"
          view={view}
          onView={(v) => setView(v)}
          views={['work_week', 'month', 'day']}
          selectable={isDozent}
          onSelectSlot={handleSelectSlot}
          onSelectEvent={handleSelectEvent}
          eventPropGetter={eventStyleGetter}
          min={new Date(0, 0, 0, 8, 0, 0)}
          max={new Date(0, 0, 0, 21, 0, 0)}
          components={{
            toolbar: CustomToolbar
          }}
          messages={{
            today: 'Heute',
            previous: 'Zurück',
            next: 'Weiter',
            month: 'Monat',
            week: 'Woche',
            day: 'Tag',
            agenda: 'Agenda',
            date: 'Datum',
            time: 'Zeit',
            event: 'Termin',
            noEventsInRange: 'Keine Termine.',
            showMore: total => `+${total} mehr`,
          }}
        />
      </div>

      <EventDetailModal
        event={selectedEvent}
        onClose={() => setSelectedEvent(null)}
        onStornieren={!isDozent ? handleStornieren : null}
      />
    </div>
  );
}
